import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../Controllers/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../Controllers/Store";
import { hasPermission } from "../../Controllers/Selectors/permissionsSelector";

import { Ripple } from "primereact/ripple";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";

import NoDataCreated from "../../Components/NoDataCreated";
import PageLoader from "../../Components/PageLoader";
import ErrorPage from "../../Components/ErrorPage";
import DocumentViewModal from "../../Components/DocumentViewModal";
import TableActionLoader from "../../Components/TableActionLoader";
import DocumentUploadModal from "../Project/CustomService/DocumentUploadModal";
import { useGoBack } from "../../Utils/Common";
import { checkFileOthers, checkText } from "../../Utils/Validator";

import { fetchFSDocumentList } from "../../Controllers/Thunks/FinishingScheduleThunks";
import { fsDocumentCreate, fsDocumentUpdate, fsDocumentDelete } from "../../Api/FinishingScheduleService/DocumentService";
import { FSFeature, FSProject } from "../../Controllers/Types/FinishingSchedule/FinishingSchedule";
import { FSDocument } from "../../Controllers/Types/FinishingSchedule/Document";

const FinishingScheduleDocument: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const goBack = useGoBack();
    const toast = useRef<Toast>(null);

    const {
        loadingFSDocuments, errorFSDocuments, fsDocuments
    } = useAppSelector((state) => state?.finishingSchedule);

    const [loading, setLoading] = useState<boolean>(false);
    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);
    const [dataState, setDataState] = useState<"Add" | "Edit">("Add");
    const [deleteLoadingId, setdeleteLoadingId] = useState<number | null>(null);

    const [fsProject, setFSProject] = useState<FSProject | null>(null);
    const [fsFeature, setFSFeature] = useState<FSFeature | null>(null);
    const [isOwnProject, setIsOwnProject] = useState<boolean>(false);

    const [showDocumentViewModal, setShowDocumentViewModal] = useState<boolean>(false);
    const [documentPath, setDocumentPath] = useState<string>("");

    const [showDocumentModal, setShowDocumentModal] = useState<boolean>(false);
    const [selectedFSDocument, setSelectedFSDocument] = useState<FSDocument | null>(null);
    const [documentName, setDocumentName] = useState<string>("");
    const [uploadedFileName, setUploadedFileName] = useState<string>("");
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);

    /* error massage */
    const [errorDocumentName, setErrorDocumentName] = useState<string>("");
    const [errorUploadedFile, setErrorUploadedFile] = useState<string>("");

    const [documentUpdateHasChanges, setDocumentUpdateHasChanges] = useState<boolean>(false);

    const canModify = (isOwnProject || fsFeature?.can_edit === true);

    /* PERMISION HANDLER */
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const hasPermissionForAddDocument = useCanEdit("Finishing Schedule", "add_document") && canModify;
    const hasPermissionForUpdateDocument = useCanEdit("Finishing Schedule", "update_document") && canModify;
    const hasPermissionForDeleteDocument = useCanEdit("Finishing Schedule", "delete_document") && canModify;

    useEffect(() => {
        if (location?.state) {
            if (location?.state?.projectData && location?.state?.projectData !== null) {
                setFSProject(location?.state?.projectData);
                setIsOwnProject(location?.state?.projectData.is_own_project!);
            }

            if (location?.state?.fsFeatre && location?.state?.fsFeatre !== null) {
                setFSFeature(location?.state?.fsFeatre);
            }
        }
    }, [location.state]);

    useEffect(() => {
        fsDocumentDataGet();
    }, [fsFeature])

    const fsDocumentDataGet = async () => {
        if (fsFeature && fsFeature !== null) {
            await dispatch(fetchFSDocumentList(fsFeature?.id));
            setInitialDataLoading(false);
        }
    }

    const handleCloseDocumentModal = () => {
        setShowDocumentModal(false);
        handleClearFields();
        handleClearErrors();
    }

    const handleClearFields = () => {
        setDocumentName('');
        setUploadedFileName("");
        setUploadedFile(null);
        setSelectedFSDocument(null);
    };

    const handleClearErrors = () => {
        setErrorDocumentName("");
        setErrorUploadedFile("");
    };

    const handleClearFile = () => {
        setUploadedFileName("");
        setUploadedFile(null);
    }

    const handleFileUpload = (file: File) => {
        setUploadedFileName(file.name);
        setUploadedFile(file);
    };

    /* Error handling */
    useEffect(() => {
        checkText(documentName, false, setErrorDocumentName);
    }, [documentName])

    useEffect(() => {
        checkFileOthers(uploadedFile!, false, setErrorUploadedFile);
    }, [uploadedFile])

    function checkCredentials(fileRequired: boolean): boolean {
        const isDocumentNameValid = checkText(documentName, true, setErrorDocumentName);
        const isUploadedFileValid = checkFileOthers(uploadedFile!, fileRequired, setErrorUploadedFile);

        return isDocumentNameValid && isUploadedFileValid;
    }

    function showAPIError(keyName: string, error: string): void {
        if (keyName === "document_name") {
            setErrorDocumentName(error);
        }

        if (keyName === "path") {
            setErrorUploadedFile(error);
        }
    }
    /*  */

    /* Add document */
    const handleAddDocument = () => {
        setShowDocumentModal(true);
        setDataState('Add');
    }

    const handleCreateDocument = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials(true)) {
            setLoading(true);
            const data = await fsDocumentCreate(
                Number(fsFeature?.id),
                documentName,
                uploadedFile!
            );
            setLoading(false);

            if (data.statusCode === 200) {
                fsDocumentDataGet();
                handleCloseDocumentModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Document added successfully.`,
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) => (
                    showAPIError(key, value[0])
                ));
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error adding the document.",
                        life: 3000,
                    });
                }
            }
        }
    };
    /*  */

    /* Edit document */
    const handleEditDocument = (document: FSDocument) => {
        setShowDocumentModal(true);
        setDataState('Edit');

        setSelectedFSDocument(document);
        setDocumentName(document?.document_name);
    }

    useEffect(() => {
        if (selectedFSDocument) {
            if (documentName !== selectedFSDocument?.document_name) {
                setDocumentUpdateHasChanges(true);
            } else if (uploadedFile !== null) {
                setDocumentUpdateHasChanges(true);
            } else {
                setDocumentUpdateHasChanges(false);
            }
        }
    }, [selectedFSDocument, documentName, uploadedFile]);

    const handleUpdateDocument = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials(false)) {
            setLoading(true);
            const data = await fsDocumentUpdate(
                selectedFSDocument?.id || 0,
                documentName,
                uploadedFile!
            );
            setLoading(false);

            if (data.statusCode === 200) {
                fsDocumentDataGet();
                handleCloseDocumentModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Document updated successfully.`,
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) => (
                    showAPIError(key, value[0])
                ));
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "An error occurred while updating the document.",
                        life: 3000,
                    });
                }
            }
        }
    };
    /*  */

    /* Delete document */
    const handleDeletedocument = (documentId: number): void => {
        confirmDialog({
            message: "Are you sure you want to delete this document?",
            header: "Confirm the deletion",
            icon: "bi bi-info-circle",
            defaultFocus: "accept",
            acceptClassName: "p-button-danger",
            accept: () => deleteDocument(documentId),
            resizable: false,
            draggable: false,
            dismissableMask: true,
        });
    };

    const deleteDocument = async (documentId: number) => {
        if (documentId) {
            setdeleteLoadingId(documentId);
            const data = await fsDocumentDelete(documentId);

            setdeleteLoadingId(null);
            if (data.statusCode === 200) {
                fsDocumentDataGet();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Document deleted successfully.`,
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "failed",
                        detail: "An error occurred while deleting the document.",
                        life: 3000,
                    });
                }
            }
        }
    };
    /*  */

    /* View document */
    const handleViewDocument = (path: string) => {
        setDocumentPath(path);
        setShowDocumentViewModal(true);
    };

    const handleCloseDocumentViewModal = () => {
        setShowDocumentViewModal(false);
        setDocumentPath('')
    };
    /*  */

    /* Table structure */
    const editDataBody = (rowData: FSDocument) => {
        const document = rowData;

        return (
            <button
                className="data_action_btn primary_table_btn p-ripple"
                onClick={() => handleEditDocument(document)}
            >
                <i className="bi bi-pencil-square"></i>
                <Ripple />
            </button>
        );
    };

    const deleteDataBody = (rowData: FSDocument) => {
        const documentId = rowData.id;
        return (
            <React.Fragment>
                {deleteLoadingId === documentId ? (
                    <TableActionLoader />
                ) : (
                    <button
                        className="data_action_btn danger_table_btn p-ripple"
                        onClick={() => handleDeletedocument(documentId)}
                    >
                        <i className="bi bi-trash3"></i>
                        <Ripple />
                    </button>
                )}
            </React.Fragment>
        );
    };

    const viewDocumentBody = (rowData: FSDocument) => {
        const documentPath = rowData.path;

        return (
            <div className="tabel_btn_area justify-content-end justify-content-lg-center mt-1 mb-1">
                <Button
                    label="View"
                    className="tabel_btn view_btn"
                    onClick={() => handleViewDocument(documentPath)}
                    disabled={rowData.path === null}
                />
            </div>
        );
    };
    /*  */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingFSDocuments && initialDataLoading ? (
                    <PageLoader />
                ) : errorFSDocuments ? (
                    <ErrorPage errorMessage={errorFSDocuments} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    Finishing schedule ({fsFeature?.feature})
                                </li>
                                <li className="page_breadcrumb_item active">
                                    Documents
                                </li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area mt-2 justify-content-end">
                            <div className="page_action_area">
                                {hasPermissionForAddDocument && (
                                    <button
                                        className="add_data_link_btn p-ripple"
                                        onClick={handleAddDocument}
                                    >
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add document</span>
                                        <Ripple />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="page_main_content_section">
                            {fsDocuments?.length > 0 ? (
                                <DataTable
                                    value={fsDocuments}
                                    paginator
                                    size="small"
                                    rows={10}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    tableStyle={{ minWidth: "50rem" }}
                                    paginatorDropdownAppendTo={"self"}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                    loading={loadingFSDocuments}
                                    scrollable
                                    dataKey="id"
                                >
                                    <Column
                                        header="Document name"
                                        headerClassName="text_purple"
                                        field="title"
                                        className="first_letter_capitalized"
                                        body={(rowData: FSDocument) => (
                                            <span>
                                                {rowData?.document_name ? rowData?.document_name : "---"}
                                            </span>
                                        )}
                                        style={{ width: "50%" }}
                                    ></Column>

                                    {hasPermissionForUpdateDocument && (
                                        <Column
                                            header="Edit"
                                            headerClassName="text_purple"
                                            body={editDataBody}
                                            alignHeader="center"
                                            className="data_action_btn_area"
                                            style={{ width: "15%" }}
                                        ></Column>
                                    )}

                                    {hasPermissionForDeleteDocument && (
                                        <Column
                                            header="Delete"
                                            headerClassName="text_purple"
                                            body={deleteDataBody}
                                            alignHeader="center"
                                            className="data_action_btn_area"
                                            style={{ width: "15%" }}
                                        ></Column>
                                    )}

                                    <Column
                                        body={viewDocumentBody}
                                        className="table_btn_area"
                                        style={{ width: "20%" }}
                                        frozen
                                        alignFrozen="right"
                                    ></Column>
                                </DataTable>
                            ) : (
                                <NoDataCreated
                                    message={`No documents added. ${hasPermissionForAddDocument ? 'To add the document click on add button.' : ''}`}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Document upload modal */}
            <DocumentUploadModal
                isOpen={showDocumentModal}
                dataState={dataState}
                onClose={handleCloseDocumentModal}
                serviceName={fsFeature?.feature}
                toast={toast}
                documentName={documentName}
                setDocumentName={setDocumentName}
                uploadedFileName={uploadedFileName}
                handleFileUpload={handleFileUpload}
                handleCreateDocument={handleCreateDocument}
                handleUpdateDocument={handleUpdateDocument}
                loading={loading}
                documentNameError={errorDocumentName}
                fileError={errorUploadedFile}
                labelName="Title"
                headerName="document"
                isFileRequired={dataState === "Add" ? true : false}
                isFileNameRequired={true}
                handleClearFile={handleClearFile}
                documentUpdateHasChanges={documentUpdateHasChanges}
            />

            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showDocumentViewModal}
                onClose={handleCloseDocumentViewModal}
                fileUrl={documentPath}
            />
        </React.Fragment>
    )
}

export default FinishingScheduleDocument;